import { useState, useEffect, useRef } from "react";
import {
  TagsInput,
  Flex,
  Text,
  ThemeIcon,
  Pill,
  ActionIcon,
} from "@mantine/core";
import axios from "axios";
import toast from "react-hot-toast";
import { IconTag, IconHash, IconEdit } from "@tabler/icons-react";

export default function Taggit({ reqInfo, fetchData = () => {} }) {
  // Return null if reqInfo doesn't contain any key values
  if (
    !reqInfo ||
    typeof reqInfo !== "object" ||
    Object.keys(reqInfo).length === 0 ||
    !Object.values(reqInfo).some(
      (val) => val !== null && val !== undefined && val !== ""
    )
  ) {
    return null;
  }

  const [data, setData] = useState([]);
  const [value, setValue] = useState([]);
  const [tagData, setTagData] = useState([]); // Store full tag data for removal
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);

  const handleValueChange = (newValue) => {
    // Find newly added tags
    const addedTags = newValue.filter((tag) => !value.includes(tag));

    addedTags.forEach((tag) => {
      // Check if tag exists in data
      const existingTag = data.find((item) => item.label === tag);
      if (existingTag) {
        onTagAdd(existingTag.value);
      } else {
        // Validate tag length before creating
        if (tag.length < 3) {
          toast.error("Tag must be at least 3 characters long");
          return; // Skip creation
        }
        // Create new tag
        onTagCreate(tag);
      }
    });

    // Find removed tags
    const removedTags = value.filter((tag) => !newValue.includes(tag));
    removedTags.forEach((tag) => {
      const tagDataItem = tagData.find((item) => item.tag.name === tag);
      if (tagDataItem) {
        onTagRemove(tagDataItem.id);
      }
    });
  };

  const CancelToken = axios.CancelToken;
  const cancelRef = useRef(null);

  useEffect(() => {
    fetchTags();
  }, []);

  function searchTags(searchValue) {
    const cancel = cancelRef.current;
    if (cancel) cancel();

    if (!searchValue) {
      setData([]);
      return;
    }

    const req = {
      ...reqInfo,
      value: searchValue,
    };

    axios
      .post(`/search-all-tags/`, req, {
        cancelToken: new CancelToken(function executor(c) {
          cancelRef.current = c;
        }),
      })
      .then(({ data }) => {
        // Remove duplicates based on label (tag name)
        const uniqueTags = data.response.reduce((acc, d) => {
          const existingTag = acc.find((tag) => tag.label === d.name);
          if (!existingTag) {
            acc.push({
              label: d.name,
              value: d.id,
            });
          }
          return acc;
        }, []);

        setData(uniqueTags);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        setData([]);
      });
  }

  function onTagAdd(e) {
    const req = {
      ...reqInfo,
      tag_id: e,
    };

    setLoading(true);
    axios
      .post(`/tags/`, req)
      .then(() => {
        toast.success("Added!");
        fetchTags();
        fetchData();
        setLoading(false);
      })
      .catch((err) => {
        toast.error(err);
        setLoading(false);
      });
  }

  function onTagCreate(tagName) {
    const req = {
      ...reqInfo,
      name: tagName,
    };

    setLoading(true);
    axios
      .post(`/tags/`, req)
      .then(() => {
        toast.success("Added!");
        fetchTags();
        fetchData();
        setLoading(false);
      })
      .catch((err) => {
        toast.error(err);
        setLoading(false);
      });
  }

  function onTagRemove(taggingId) {
    setLoading(true);
    axios
      .post(`/delete-tagging/`, { tagging_id: taggingId })
      .then(() => {
        fetchTags();
        fetchData();
        setLoading(false);
      })
      .catch((err) => {
        toast.error(err);
        setLoading(false);
      });
  }

  function fetchTags() {
    setLoading(true);

    axios
      .post(`/retrieve-entity-tags/`, reqInfo)
      .then(({ data }) => {
        setLoading(false);
        setValue(data.response.map((m) => m.tag.name));
        setTagData(data.response); // Store full tag data for removal
      })
      .catch((err) => {
        setLoading(false);
        setValue([]);
        setTagData([]);
      });
  }

  if (editing) {
    return (
      <Flex align="center" gap="xs">
        <TagsInput
          placeholder="Search tags"
          style={{ flexGrow: 1 }}
          value={value}
          onChange={handleValueChange}
          data={data.map((item) => item.label)}
          searchable
          creatable
          getCreateLabel={(query) => `+ Create ${query}`}
          onCreate={(query) => {
            if (query.length < 3) {
              toast.error("Tag must be at least 3 characters long");
              return null; // Prevents creation
            }
            onTagCreate(query);
            return query;
          }}
          onSearchChange={searchTags}
          size="sm"
          radius="lg"
          styles={{
            input: {
              // border: "1px solid var(--mantine-color-gray-3)",
              "&:focus": {
                border: "1px solid var(--mantine-color-pink-5)",
              },
              "&:focusWithin": {
                border: "1px solid var(--mantine-color-pink-5)",
              },
            },
          }}
        />
        <ActionIcon
          size="xs"
          color="gray"
          radius="xl"
          onClick={() => setEditing(!editing)}
          variant="transparent"
        >
          <IconEdit size={14} />
        </ActionIcon>
      </Flex>
    );
  }

  return (
    <Flex align="center" gap="xs">
      <Flex gap="xs" wrap="wrap" align="center">
        {tagData.length === 0 && <Text size="sm">start tagging</Text>}
        {tagData.map((m) => (
          <TagPill tag={m.tag} key={m.id} />
        ))}
      </Flex>
      <ActionIcon
        size="xs"
        color="gray"
        radius="xl"
        onClick={() => setEditing(!editing)}
        variant="transparent"
      >
        <IconEdit size={14} />
      </ActionIcon>
    </Flex>
  );
}

export const TagPill = ({ tag }) => {
  return (
    <Pill
      styles={{
        root: {
          backgroundColor: "var(--mantine-color-gray-2)",
        },
      }}
      color="pink"
      size="sm"
      fw={500}
    >
      {tag.name}
    </Pill>
  );
};

export const TagPillList = ({ tags }) => {
  if (tags.length === 0) {
    return <Text size="sm">no tags</Text>;
  }

  return (
    <Flex gap="xs" wrap="wrap" align="center">
      {tags.map((m) => (
        <TagPill tag={m} key={m.id} />
      ))}
    </Flex>
  );
};
